import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { propertyApi } from '../services/propertyApi';
import { isUserPaidSubscriptionActive } from '../utils/subscriptionUtils';
import { isNativeMobileApp } from '../utils/mobileApp';
import { openLandlordPackageManagement, SUBSCRIPTION_RENEWAL_PATH } from '../utils/subscriptionRenewalNotification';

/** Chỉ ẩn tạm khi đang đăng nhập hoặc đang chọn/thanh toán gói. Ra khỏi các trang này là hiện lại. */
const ALLOW_PAY_PATHS = [
  '/account-management',
  '/upgrade',
];

const AUTH_PATHS = [
  '/login',
  '/auth/login',
  '/auth/register',
  '/register',
  '/mobile/login',
  '/auth/kyc',
  '/auth/kyc-status',
];

const SubscriptionRenewalModal: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [listingCount, setListingCount] = useState(0);
  const [backendSubscriptionActive, setBackendSubscriptionActive] = useState(false);

  const paidActive = isUserPaidSubscriptionActive(user) || backendSubscriptionActive;
  const needsRenewal =
    !isLoading &&
    isAuthenticated &&
    !!user &&
    user.accountType === 'landlord' &&
    !paidActive &&
    listingCount > 0;

  const onAuthPage = AUTH_PATHS.some((path) => location.pathname.startsWith(path));
  const onPayPage = ALLOW_PAY_PATHS.some((path) => location.pathname.startsWith(path));
  const open = needsRenewal && !onAuthPage && !onPayPage;

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user || user.accountType !== 'landlord') {
      setListingCount(0);
      setBackendSubscriptionActive(false);
      return;
    }
    if (isUserPaidSubscriptionActive(user)) {
      setListingCount(0);
      setBackendSubscriptionActive(true);
      return;
    }

    let cancelled = false;
    propertyApi.getRentalLimitInfo().then((res) => {
      if (cancelled) return;
      setListingCount(res.data?.rental_count ?? 0);
      setBackendSubscriptionActive(Boolean(res.data?.subscription_active));
    }).catch(() => {
      if (!cancelled) {
        setListingCount(0);
        setBackendSubscriptionActive(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, isLoading, user?.id, user?.accountType, user?.subscriptionLevel, user?.subscriptionEndDate]);

  useEffect(() => {
    if (!open) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const blockEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockEscape, true);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', blockEscape, true);
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscription-renewal-title"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 sm:p-8">
        <div className="w-14 h-14 rounded-full bg-[#C4A35A] flex items-center justify-center mx-auto mb-4">
          <i className="fas fa-clock text-white text-xl" />
        </div>
        <h3 id="subscription-renewal-title" className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-2">
          Gói đã hết hạn
        </h3>
        <p className="text-sm sm:text-base text-gray-600 text-center mb-6">
          Hãy tiếp tục gia hạn để sử dụng.
        </p>
        <button
          type="button"
          onClick={() => {
            if (isNativeMobileApp()) {
              openLandlordPackageManagement();
              return;
            }
            navigate(SUBSCRIPTION_RENEWAL_PATH);
          }}
          className="w-full py-3 bg-gradient-to-r from-black to-gray-800 text-white rounded-full font-semibold text-sm sm:text-base hover:shadow-lg transition-all"
        >
          Gia hạn tiếp
        </button>
      </div>
    </div>
  );
};

export default SubscriptionRenewalModal;
